<!--
@component Navbar.Item

A component representing an item in the navigation bar. It can contain a link and optionally a megamenu or submenu.

@prop href - The URL that the item links to
@prop label - The text label for the item
@prop colorVariant - The color variant for the item
@prop active - Whether the item is currently active
@prop rootStyles - Custom styles for the root element
@prop labelStyles - Custom styles for the label element
@prop iconStyles - Custom styles for the icon element

Example:
<Navbar.Item href="/products" label="Products">
  <Navbar.MegaMenu>
    <Navbar.MegaMenuColumn>
      <Navbar.MegaMenuHeader title="Category 1" />
      <Navbar.MegaMenuLink href="/subcategory1" label="Subcategory 1" />
      <Navbar.MegaMenuLink href="/subcategory2" label="Subcategory 2" />
    </Navbar.MegaMenuColumn>
  </Navbar.MegaMenu>
</Navbar.Item>
-->

<script lang="ts">
  import type { NavbarStore } from '../navbar-store.svelte';
  import type { NavbarItemProps, NavbarItemContext } from '../navbar.types';
  import { getContext, setContext } from 'svelte';
  import { CaretDown } from 'phosphor-svelte';
  import { css } from '@agapi/ui/panda/css';
  import { dynamicColorStyles } from '@agapi/ui/panda/utils';
  import { navbarItemStyles } from '../navbar.styles';

  const { children, rootStyles, labelStyles, iconStyles, href = '#', colorVariant, active = false, label, enabled = true }: NavbarItemProps = $props();

  const navbarContext = getContext<NavbarStore>('navbar');

  const id = $props.id();

  const HOVER_DELAY = 193;
  const CLOSE_DELAY = 350;

  let keepClosed = $state(false);

  /**
   * Handles mouse enter event for the navbar item.
   * Opens the submenu after a delay if the item has children.
   */
  const onmouseenter = () => {
    if (!children) { return }
    if (navbarContext.closeTimeoutId) {
      clearTimeout(navbarContext.closeTimeoutId);
      navbarContext.setCloseTimeout(null);
    }

    const timeout = setTimeout(() => {
      if (keepClosed) { return }
      navbarContext.setOpenItem(id);
    }, HOVER_DELAY);

    navbarContext.setHoverTimeout(timeout as unknown as number);
  }

  /**
   * Handles mouse leave event for the navbar item.
   * Closes the submenu after a delay if the item has children.
   */
  const onmouseleave = () => {
    if (!children) {
      return;
    }

    if (navbarContext.hoverTimeoutId) {
      clearTimeout(navbarContext.hoverTimeoutId);
      navbarContext.setHoverTimeout(null);
    }

    const timeout = setTimeout(() => {
      navbarContext.setOpenItem(null);
    }, CLOSE_DELAY);

    navbarContext.setCloseTimeout(timeout as unknown as number);
  }

  /**
   * Handles focus event for the navbar item.
   * Closes the search component when the item is focused.
   */
  const onfocus = () => {
    navbarContext.closeSearch();
  }

  /**
   * Handles blur event for the navbar item.
   * Currently empty, but can be used for additional functionality.
   */
  const onblur = () => {
    // Do nothing for now
  }

  /**
   * Handles keydown events for the navbar item.
   * Implements keyboard navigation for accessibility.
   * @param {KeyboardEvent} event - The keydown event
   */
  const handleEnter = (event: KeyboardEvent) => {
    if (!children) return;
    if (navbarContext.openItemId === id) {
      navbarContext.setCloseTimeout(null);
      navbarContext.setOpenItem(null);
    } else {
      event.preventDefault();
      navbarContext.setCloseTimeout(null);
      navbarContext.setOpenItem(id);
    }
  }

  const handleEscape = () => {
    navbarContext.setOpenItem(null);
  }

  const handleArrowRight = (element: HTMLAnchorElement) => {
    (element.parentElement?.nextElementSibling?.children[0] as HTMLElement)?.focus();
  }

  const handleArrowLeft = (element: HTMLAnchorElement) => {
    (element.parentElement?.previousElementSibling?.children[0] as HTMLElement)?.focus();
  }

  const handleArrowDown = (element: HTMLAnchorElement) => {
    navbarContext.setCloseTimeout(null);
    navbarContext.setOpenItem(id);
    (element.parentElement?.children[1].children[0].children[0].children[0] as HTMLElement)?.focus();
  }

  const onkeydown = (event: KeyboardEvent) => {
    const element = event.target as unknown as HTMLAnchorElement;
    switch (event.key) {
      case 'Enter':
        handleEnter(event);
        break;
      case 'Escape':
        handleEscape();
        break;
      case 'ArrowRight':
        handleArrowRight(element);
        break;
      case 'ArrowLeft':
        handleArrowLeft(element);
        break;
      case 'ArrowDown':
        handleArrowDown(element);
        break;
    }
  }

  const onclick = () => {
    navbarContext.setOpenItem(null);
    navbarContext.setHoverTimeout(null);
    navbarContext.setCloseTimeout(null);
    keepClosed = true;
    setTimeout(() => {
      keepClosed = false;
    }, CLOSE_DELAY);
  }

  let classes = $derived(navbarItemStyles.raw({ active, open: navbarContext.openItemId === id }));

  setContext<NavbarItemContext>('navbar-item', {
    id,
    hoverDelay: HOVER_DELAY,
    closeDelay: CLOSE_DELAY,
  });
</script>

<div
  class={css(classes.root, rootStyles)}
  role="menuitem"
  style="
    {colorVariant ? `background-color: ${dynamicColorStyles(colorVariant).backgroundColor};` : ''}
    {colorVariant ? `color: ${dynamicColorStyles(colorVariant).color};` : ''}
  "
  aria-expanded={navbarContext.openItemId === id}
>
  <a
    {onmouseenter}
    {onmouseleave}
    {onfocus}
    {onblur}
    {onkeydown}
    {onclick}
    href={href}
    class={css(classes.label, labelStyles)}
    data-id={id}
    aria-expanded={navbarContext.openItemId === id}
    aria-haspopup={!!children}
    aria-controls={children ? `submenu-${id}` : undefined}
    style={enabled ? undefined : 'pointer-events: none;'}
  >
    {label}

    {#if children}
      <CaretDown class={css(classes.icon, iconStyles)} />
    {/if}
  </a>

  {#if children}
    {@render children()}
  {/if}
</div>